/**
 * UserAvatar — a user's round profile thumbnail. Resolves the avatar catalog
 * id either from the explicit `avatarKey` prop or from the profile directory
 * by `uid`, then renders the PROFILE_AVATARS art. A user without a chosen
 * avatar (unclaimed stub / empty avatarKey) gets a muted generic silhouette —
 * never a random pigeon.
 */
import { PROFILE_AVATARS } from './avatarManifest.js';
import { useProfileByUid } from '../services/context.js';

export interface UserAvatarProps {
  /** Directory lookup key (used when `avatarKey` is not given). */
  uid?: string;
  /** Explicit catalog id ('' / null = no avatar). Wins over the uid lookup. */
  avatarKey?: string | null;
  /** Rendered size in px (square). Default 24. */
  size?: number;
  alt?: string;
}

export function UserAvatar({ uid, avatarKey, size = 24, alt }: UserAvatarProps) {
  const profile = useProfileByUid(avatarKey === undefined ? uid : undefined);
  const key = avatarKey !== undefined ? avatarKey : profile?.avatarKey;
  const art = key ? PROFILE_AVATARS.find((a) => a.id === key) : undefined;

  if (!art) {
    // Muted inline silhouette placeholder (no avatar chosen).
    return (
      <svg
        className="avatar avatar--empty"
        viewBox="0 0 24 24"
        width={size}
        height={size}
        role="img"
        aria-label={alt ?? 'No avatar'}
        data-testid="user-avatar"
      >
        <circle cx="12" cy="9" r="4.2" />
        <path d="M3.8 21c0-4.1 3.7-6.6 8.2-6.6s8.2 2.5 8.2 6.6z" />
      </svg>
    );
  }

  return (
    <img
      className="avatar"
      src={art.src}
      width={size}
      height={size}
      alt={alt ?? art.alt}
      data-testid="user-avatar"
      data-avatar-key={art.id}
    />
  );
}
